import i18next from "i18next";

import {
	DialogType,
	StatusTransition,
	StatusTransitionType,
} from "../types/transition-types";

// Status constants for readability
export const STATUS = {
	SCHEDULED: "SCH",
	REASSIGNED: "RAS",
	TRAINING: "TRA",
	CALLED_OFF: "COF",
	RESTRICTED_DUTY: "RDU",
} as const;

// Define all allowed status transitions
export const statusTransitions: StatusTransition[] = [
	// From any status (except Called Off) to Scheduled
	{
		fromStatus: STATUS.REASSIGNED,
		toStatus: STATUS.SCHEDULED,
		type: StatusTransitionType.SIMPLE,
		dialogType: DialogType.CONFIRMATION,
		title: "full-time-worker-scheduler_mark-as-scheduled",
		message: "full-time-worker-scheduler_mark-as-scheduled-message",
	},
	{
		fromStatus: STATUS.TRAINING,
		toStatus: STATUS.SCHEDULED,
		type: StatusTransitionType.SIMPLE,
		dialogType: DialogType.CONFIRMATION,
		title: "full-time-worker-scheduler_mark-as-scheduled",
		message: "full-time-worker-scheduler_mark-as-scheduled-message",
	},
	{
		fromStatus: STATUS.RESTRICTED_DUTY,
		toStatus: STATUS.SCHEDULED,
		type: StatusTransitionType.SIMPLE,
		dialogType: DialogType.CONFIRMATION,
		title: "full-time-worker-scheduler_mark-as-scheduled",
		message: "full-time-worker-scheduler_mark-as-scheduled-message",
	},

	// From any status (except Called Off) to Restricted Duty
	{
		fromStatus: STATUS.SCHEDULED,
		toStatus: STATUS.RESTRICTED_DUTY,
		type: StatusTransitionType.SIMPLE,
		dialogType: DialogType.CONFIRMATION,
		title: "full-time-worker-scheduler_mark-as-restricted-duty",
		message: "full-time-worker-scheduler_mark-as-restricted-duty-message",
	},
	{
		fromStatus: STATUS.REASSIGNED,
		toStatus: STATUS.RESTRICTED_DUTY,
		type: StatusTransitionType.SIMPLE,
		dialogType: DialogType.CONFIRMATION,
		title: "full-time-worker-scheduler_mark-as-restricted-duty",
		message: "full-time-worker-scheduler_mark-as-restricted-duty-message",
	},
	{
		fromStatus: STATUS.TRAINING,
		toStatus: STATUS.RESTRICTED_DUTY,
		type: StatusTransitionType.SIMPLE,
		dialogType: DialogType.CONFIRMATION,
		title: "full-time-worker-scheduler_mark-as-restricted-duty",
		message: "full-time-worker-scheduler_mark-as-restricted-duty-message",
	},

	// From any status (except Called Off) to Training
	{
		fromStatus: STATUS.SCHEDULED,
		toStatus: STATUS.TRAINING,
		type: StatusTransitionType.SIMPLE,
		dialogType: DialogType.CONFIRMATION,
		title: "full-time-worker-scheduler_mark-as-in-training",
		message: "full-time-worker-scheduler_mark-as-in-training-message",
	},
	{
		fromStatus: STATUS.REASSIGNED,
		toStatus: STATUS.TRAINING,
		type: StatusTransitionType.SIMPLE,
		dialogType: DialogType.CONFIRMATION,
		title: "full-time-worker-scheduler_mark-as-in-training",
		message: "full-time-worker-scheduler_mark-as-in-training-message",
	},
	{
		fromStatus: STATUS.RESTRICTED_DUTY,
		toStatus: STATUS.TRAINING,
		type: StatusTransitionType.SIMPLE,
		dialogType: DialogType.CONFIRMATION,
		title: "full-time-worker-scheduler_mark-as-in-training",
		message: "full-time-worker-scheduler_mark-as-in-training-message",
	},

	// From any status (except Called Off) to Reassigned
	{
		fromStatus: STATUS.SCHEDULED,
		toStatus: STATUS.REASSIGNED,
		type: StatusTransitionType.COMPLEX,
		dialogType: DialogType.REASSIGNMENT,
		requiresInput: true,
	},
	{
		fromStatus: STATUS.TRAINING,
		toStatus: STATUS.REASSIGNED,
		type: StatusTransitionType.COMPLEX,
		dialogType: DialogType.REASSIGNMENT,
		requiresInput: true,
	},
	{
		fromStatus: STATUS.RESTRICTED_DUTY,
		toStatus: STATUS.REASSIGNED,
		type: StatusTransitionType.COMPLEX,
		dialogType: DialogType.REASSIGNMENT,
		requiresInput: true,
	},

	// From any status (except Called Off) to Called Off
	{
		fromStatus: STATUS.SCHEDULED,
		toStatus: STATUS.CALLED_OFF,
		type: StatusTransitionType.COMPLEX,
		dialogType: DialogType.CALL_OFF,
		requiresInput: true,
	},
	{
		fromStatus: STATUS.REASSIGNED,
		toStatus: STATUS.CALLED_OFF,
		type: StatusTransitionType.COMPLEX,
		dialogType: DialogType.CALL_OFF,
		requiresInput: true,
	},
	{
		fromStatus: STATUS.TRAINING,
		toStatus: STATUS.CALLED_OFF,
		type: StatusTransitionType.COMPLEX,
		dialogType: DialogType.CALL_OFF,
		requiresInput: true,
	},
	{
		fromStatus: STATUS.RESTRICTED_DUTY,
		toStatus: STATUS.CALLED_OFF,
		type: StatusTransitionType.COMPLEX,
		dialogType: DialogType.CALL_OFF,
		requiresInput: true,
	},

	// From Called Off back to previous status (special handling)
	{
		fromStatus: STATUS.CALLED_OFF,
		toStatus: STATUS.SCHEDULED,
		type: StatusTransitionType.COMPLEX,
		dialogType: DialogType.REMOVE_CALL_OFF,
		title: "full-time-worker-scheduler_remove-time-off",
		message: "full-time-worker-scheduler_remove-time-off-message-scheduled",
	},
	{
		fromStatus: STATUS.CALLED_OFF,
		toStatus: STATUS.REASSIGNED,
		type: StatusTransitionType.COMPLEX,
		dialogType: DialogType.REMOVE_CALL_OFF,
		title: "full-time-worker-scheduler_remove-time-off",
		message: "full-time-worker-scheduler_remove-time-off-message-reassigned",
	},
	{
		fromStatus: STATUS.CALLED_OFF,
		toStatus: STATUS.TRAINING,
		type: StatusTransitionType.COMPLEX,
		dialogType: DialogType.REMOVE_CALL_OFF,
		title: "full-time-worker-scheduler_remove-time-off",
		message: "full-time-worker-scheduler_remove-time-off-message-training",
	},
	{
		fromStatus: STATUS.CALLED_OFF,
		toStatus: STATUS.RESTRICTED_DUTY,
		type: StatusTransitionType.COMPLEX,
		dialogType: DialogType.REMOVE_CALL_OFF,
		title: "full-time-worker-scheduler_remove-time-off",
		message: "full-time-worker-scheduler_remove-time-off-message-restricted-duty",
	},
];

// Resolves the translation keys stored in title/message into rendered text.
// Done at call-time so it reacts to language changes on re-render.
function resolveTransition(transition: StatusTransition): StatusTransition {
	return {
		...transition,
		...(transition.title ? { title: i18next.t(transition.title) } : {}),
		...(transition.message ? { message: i18next.t(transition.message) } : {}),
	};
}

// Helper functions
export function getTransition(
	fromStatus: string,
	toStatus: string
): StatusTransition | undefined {
	const transition = statusTransitions.find(
		(transition) =>
			transition.fromStatus === fromStatus && transition.toStatus === toStatus
	);
	return transition ? resolveTransition(transition) : undefined;
}

export function getAllowedTransitions(
	fromStatus: string,
	previousStatus?: string
): StatusTransition[] {
	if (fromStatus === STATUS.CALLED_OFF && previousStatus) {
		// For Called Off, only allow returning to previous status
		return statusTransitions
			.filter(
				(transition) =>
					transition.fromStatus === STATUS.CALLED_OFF &&
					transition.toStatus === previousStatus
			)
			.map(resolveTransition);
	}

	return statusTransitions
		.filter((transition) => transition.fromStatus === fromStatus)
		.map(resolveTransition);
}

export function isTransitionAllowed(
	fromStatus: string,
	toStatus: string
): boolean {
	return getTransition(fromStatus, toStatus) !== undefined;
}
